import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { DetroitGameState } from '../../types';
import { buildTrack } from '../moves/buildTrack';
import { passBuildTrack, updateBuildCostInfo } from '../moves/passBuildTrack';
import { cancelAllBuildTrack } from '../moves/cancelAllBuildTrack';
import { buildSpecialLink } from '../moves/buildSpecialLink';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 4: 線路建設フェーズ（Detroit専用）
 *
 * Detroit固有のエンジニアボーナスは MapConfig.hasEngineerBonus で適用:
 * - ENGINEERアクション選択時、最安タイルが無料
 *
 * @see docs/specifications/logic/detroit_bankruptcy_solo/phase_04_build_track.md
 */
export const buildTrackDetroitPhase: PhaseConfig<DetroitGameState> = {
  moves: {
    buildTrack,
    passBuildTrack,  // 共通Move（MapConfig.hasEngineerBonusで割引適用）
    cancelAllBuildTrack,
    buildSpecialLink,
    undoToTurnStart,
  },

  turn: {
    minMoves: 0,
    maxMoves: 4
  },

  onBegin: ({ G, ctx }: { G: DetroitGameState; ctx: Ctx }) => {
    debug.log('[buildTrackDetroitPhase] Phase 4: Build Track started');

    // 未完成区間の所有権チェック
    G.unfinishedSections = G.unfinishedSections.filter(section => {
      if (section.tilesAddedSinceLastTurn === 0 && section.owner === ctx.currentPlayer) {
        debug.log(`[buildTrackDetroitPhase] Section ${section.id} ownership lost`);
        return false;
      }
      section.tilesAddedSinceLastTurn = 0;
      return true;
    });

    // Cancel操作用スナップショット
    G.buildTrackPhaseSnapshot = {
      completedLinks: JSON.parse(JSON.stringify(G.completedLinks)),
      unfinishedSections: JSON.parse(JSON.stringify(G.unfinishedSections)),
      ferryLinkOwners: G.ferryLinks?.map(l => ({ id: l.id, owner: l.owner })) || [],
      adjacentLinkOwners: G.adjacentLinks?.map(l => ({ id: l.id, owner: l.owner })) || [],
    };
    debug.log(`[buildTrackDetroitPhase] Snapshot saved: ${G.buildTrackPhaseSnapshot.completedLinks.length} links, ${G.buildTrackPhaseSnapshot.unfinishedSections.length} sections`);

    // プレイヤーごとの配置枚数リセット
    Object.values(G.players).forEach(player => {
      player.tilesPlacedThisTurn = 0;
      player.newCityTilesPlacedThisTurn = 0;
      player.hasPassedBuild = false;
      player.buildTrackHistory = [];
    });

    // 建設コスト情報を初期化（UI表示用）
    updateBuildCostInfo(G, ctx.currentPlayer);
  },

  onEnd: ({ G }: { G: DetroitGameState }) => {
    debug.log('[buildTrackDetroitPhase] Phase 4 ended');

    // Phase 5 用に hasPassed をリセット（Phase 3 で true になっている可能性がある）
    Object.values(G.players).forEach(player => {
      player.hasPassed = false;
    });
  },

  endIf: ({ G }: { G: DetroitGameState; ctx: Ctx }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[buildTrackDetroitPhase] Force transition to issueShares (Undo)');
      return true;
    }

    const allPlayersCompleted = Object.values(G.players).every(player => {
      if (player.isBankrupt) return true;
      return player.hasPassedBuild;
    });

    if (allPlayersCompleted) {
      debug.log('[buildTrackDetroitPhase] All players completed, ending phase');
      return true;
    }

    return false;
  },

  // Phase 5 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: DetroitGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'moveGoods';
  }
};
